import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import { API_MANAGEMENT_ORDERS_V2 } from '@/constants/urls'
import { axiosWcgw } from '@/services/http/axiosApi'
import { STATUS_ERROR_PAGE } from '@/constants/urls'
import { ORDERS_FIELDS } from '@/constants/urls'
import { OrderItemsFilter, OrderFilter } from '@/constants/urls'
import { now, today } from '@/helpers/date-helpers'
import { type Order, type OrderItem } from '@/stores/orders.types'
import dayjs from 'dayjs'
import { bffClient } from '@ecom/customer-common-front'
import { gql } from 'graphql-request'
import { BOOKING_STATUS_ENUM } from '@/constants/bookings'
import { getCurrency } from '@/helpers/currency'

const makeFilter = (filters: Record<string, string[]>) =>
  Object.entries(filters)
    .map((pairs) => `${pairs[0]}:${pairs[1].join('+')}`)
    .join(';')

const getBookings = async (staticParams: any, dynamicParams: any) => {
  try {
    const { data } = await axiosWcgw.get(API_MANAGEMENT_ORDERS_V2, {
      params: {
        currency: getCurrency(),
        fields: String(ORDERS_FIELDS),
        ...staticParams,
        ...dynamicParams
      }
    })
    return data?.orders
  } catch (e) {
    console.error(e)
    window.location.href = STATUS_ERROR_PAGE
  }
}

const getUpcomingBookings = (pagination = {}): Promise<Array<Order>> =>
  getBookings(
    {
      sortType: 'asc',
      getStays: false,
      startDate: today,
      filter: makeFilter({
        orderItems: [
          OrderItemsFilter.isHotelBooking,
          OrderItemsFilter.hasHotelName,
          OrderItemsFilter.isStatusActive
        ],
        $: [OrderFilter.isOrderItemsNonEmpty]
      })
    },
    pagination
  )

const getPastBookings = (pagination = {}): Promise<Array<Order>> =>
  getBookings(
    {
      getStays: true,
      filter: makeFilter({
        orderItems: [
          OrderItemsFilter.isHotelBooking,
          OrderItemsFilter.hasHotelName,
          `(${[OrderItemsFilter.isStatusNotActive, OrderItemsFilter.isArrivalDateLTToday].join(
            '|'
          )})`
        ],
        $: [OrderFilter.isOrderItemsNonEmpty]
      })
    },
    pagination
  )

export interface ExperienceItem {
  id: string
  title: string
  url: string
  mainPictureUrl: string
  pricing: {
    startingPrice: number
    currency: string
    rewardPoints: number
  }
  rating: {
    rating: number
    outOf: number
  } | null
}

export interface ActivitiesData {
  booking: {
    hotel: {
      experiences: {
        paginationCursor: string
        items: Array<ExperienceItem>
      }
    }
  }
}

export const useOrderStore = defineStore('orders', () => {
  const activeBookings: Ref<Array<Order>> = ref([])
  const pastBookings: Ref<Array<Order>> = ref([])
  const feverActivities: Ref<Array<ExperienceItem>> = ref([])

  const loadPastAndCanceledBooking = async (paginate = true, limit = 5) => {
    try {
      const bookings = await getPastBookings({
        limit,
        offset: paginate ? pastBookings.value.length : 0
      })
      pastBookings.value = paginate ? [...pastBookings.value, ...bookings] : bookings
      return { value: pastBookings.value, hasNext: bookings.length === limit }
    } catch (err) {
      console.error(err)
    }
  }

  const loadUpcomingBooking = async (paginate = true) => {
    try {
      const bookings = await getUpcomingBookings({
        offset: paginate ? activeBookings.value.length : 0
      })
      activeBookings.value = paginate ? [...activeBookings.value, ...bookings] : bookings
      return { value: activeBookings.value, hasNext: false }
    } catch (err) {
      console.error(err)
    }
  }

  const getFeverActivities = async (
    bookingId: string,
    dateFrom?: string,
    dateTo?: string,
    limit = 3
  ) => {
    const queryBff = gql`
      query ActivitiesQuery($number: String!, $dateFrom: Date, $dateTo: Date, $limit: Int!) {
        booking(number: $number) {
          hotel {
            experiences(dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit) {
              items {
                id
                title
                url
                mainPictureUrl
                pricing {
                  startingPrice
                  currency
                  rewardPoints
                }
              }
            }
          }
        }
      }
    `

    const resultQuery: ActivitiesData = await bffClient().request(queryBff, {
      number: bookingId,
      dateFrom,
      dateTo,
      limit
    })
    feverActivities.value = resultQuery?.booking?.hotel?.experiences?.items || []

    return feverActivities
  }

  const isCurrentBooking = (item: OrderItem): boolean => {
    if (!item.hotelBooking) return false
    return (
      dayjs(item.hotelBooking.reservation.stayInfo.arrivalDate).isSameOrBefore(now()) &&
      dayjs(item.hotelBooking.reservation.stayInfo.departureDate).isAfter(now()) &&
      item.hotelBooking.status !== BOOKING_STATUS_ENUM.CANCELLED
    )
  }

  const currentBooking = computed<Order[]>(() =>
    pastBookings.value.filter((item: Order) =>
      item.orderItems.some((order) => isCurrentBooking(order))
    )
  )

  return {
    activeBookings,
    currentBooking,
    pastBookings,
    loadUpcomingBooking,
    loadPastAndCanceledBooking,
    feverActivities,
    getFeverActivities
  }
})
